import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Message, Popover, Select, Tooltip } from '@arco-design/web-react';
import { listCollectFields, requestCollect, type CollectFieldItem } from '../../api/assist';
import { useCollectGate } from '../../store/collectGate';
import { useCtxStore } from '../../pages/workbench/context/store';

/**
 * 顶栏「收号」入口（文字按钮，挂在举手/铃铛一列）：点开是收号字段下拉
 * （`GET /assist/collect-fields` 的启用字段）。用「收号」两个字直白说明入口，不再用图标。
 * 形态与顶栏相邻入口同一行高，弱化色、悬浮转主色。
 *
 * 选中即按**当前激活 Tab** 把该字段的采集请求下发：通话 Tab 走 `POST /assist/collect/request`
 * （后端转 call 进程起 DTMF 采集，客户按语音提示按键、`#` 确认）；会话 Tab 走 WS `chat_flow_op`
 * （op=agent_collect，向访客下发输入卡片）。值永远由客户给，坐席不代填；已收到的值在
 * 「查看已收号」抽屉里看（内容 = 原左栏「收号信息」卡，同样按当前 Tab 展示通话/会话收号）。
 *
 * 为什么这里不放输入框：下发收号是「挑一个字段」的动作，放输入框会被当成坐席代录，
 * 也会把 PII 直接摊在顶栏上（这正是左栏收号卡改按需展开的原因）。
 *
 * **显隐**：没在打电话、也没有进行中的会话时不渲染（判据见下方 `callUUID / chatId` 早退）——
 * 此时下发没有对象，入口点了只会弹「当前无进行中通话 / 会话」。
 */
export default function CollectSendButton({ wsSend }: { wsSend?: (obj: any) => boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const openDrawer = useCollectGate((s) => s.openDrawer);
  // 当前激活 Tab（通话/会话）与选中会话：工作台语境 store 的单一真源，据此决定收号下发走哪条通道
  const view = useCtxStore((s) => s.view);
  const chatId = useCtxStore((s) => s.chatId);
  /** 本通在途通话 uuid（接通后由工作台写回语境 store，挂断清空） */
  const callUUID = useCtxStore((s) => s.callUUID);
  const [open, setOpen] = useState(false);
  // 悬浮提示自己控显（原因见下方 Tooltip 处注释：不能靠 Tooltip.disabled 收提示）
  const [tipOpen, setTipOpen] = useState(false);
  const [catalog, setCatalog] = useState<CollectFieldItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listCollectFields();
      // 目录接口异常形态（非数组）一律按空目录处理：正文在渲染期 .map，不兜会直接把浮层渲染打崩
      setCatalog(Array.isArray(list) ? list : []);
    } catch {
      /* 拦截器已提示 */
    } finally {
      setLoading(false);
    }
  }, []);

  // 每次展开都重拉字段目录：目录由管理员在「收号管理」维护，坐席侧不宜长期缓存
  useEffect(() => {
    if (open) void loadCatalog();
  }, [open, loadCatalog]);

  // 没在打电话（无在途通话 uuid）、也没有进行中的会话（无选中会话）时不显示本入口：
  // 此刻两条下发通道都没有对象，点开只会弹「当前无进行中通话 / 会话」，占着顶栏还容易被误当可用功能。
  // 判据取语境 store 的 callUUID / chatId —— 与下方 send() 两个通道各自的前置条件是同一真源，
  // 保证「看得见 = 点得动」；接通/认领会话即出现，挂断/会话结束即消失。
  // （本 return 在所有 Hook 之后：不改变 Hook 调用顺序，见 scripts/check-hooks.mjs。）
  if (!callUUID && !chatId) return null;

  /** 按当前激活 Tab 下发收号（通话=DTMF 采集，会话=向访客下发输入卡片） */
  const send = async (key: string) => {
    const field = catalog.find((f) => f.key === key);
    if (!field) return;
    // 收号按「当前语境」下发，而语境只在工作台页维护：不在工作台先回去，再让坐席选一次
    // 工作台是统一入口（电话/在线界面同页）：在 /workbench 上直接下发，不在才先回工作台
    const onWorkbench = location.pathname === '/workbench';
    if (!onWorkbench) {
      navigate('/workbench');
      Message.info('已切到工作台，请再次选择收号字段');
      return;
    }
    // 会话 Tab：走在线通道 chat_flow_op，按当前会话 id 下发；否则会把无通话误判成「无进行中通话」
    if (view === 'chat') {
      if (!chatId) {
        Message.warning('当前无进行中的会话，收号请求未下发');
        return;
      }
      if (!wsSend?.({ type: 'chat_flow_op', data: { sessionId: chatId, op: 'agent_collect', field_key: field.key } })) {
        Message.warning('管理台 WebSocket 未连接，收号请求未下发');
        return;
      }
      setOpen(false);
      Message.success(`已下发「${field.label}」，请引导访客填写`);
      return;
    }
    // 通话 Tab：走语音收号 API
    const uuid = useCtxStore.getState().callUUID;
    if (!uuid) {
      Message.warning('当前无进行中通话，收号请求未下发');
      return;
    }
    try {
      await requestCollect({ uuid, field_key: field.key });
      setOpen(false);
      Message.success(`已下发「${field.label}」，请引导客户按提示输入`);
    } catch {
      /* 拦截器已提示 */
    }
  };

  /** 看客户已提交的收号值（工作台抽屉，卡内含收号清单与手动录入） */
  const viewCollected = () => {
    setOpen(false);
    if (location.pathname !== '/workbench') navigate('/workbench');
    openDrawer();
  };

  const panel = (
    <div className="bcc-collect-send">
      <div className="bcc-collect-send-title">发送收号</div>
      <Select
        size="small"
        placeholder={loading ? '加载字段目录…' : '选择收号字段…'}
        loading={loading}
        options={catalog.map((f) => ({ label: f.label, value: f.key }))}
        onChange={(v) => void send(String(v))}
      />
      <span className="bcc-collect-send-tip">选中后向客户下发采集请求，值由客户输入，坐席不代填。</span>
      <Button size="mini" type="text" onClick={viewCollected}>
        查看已收号
      </Button>
    </div>
  );

  return (
    <Popover trigger="click" position="br" content={panel} popupVisible={open} onVisibleChange={setOpen}>
      {/* 浮层需要「能透传 ref 的单个子元素」，故 Popover 子节点是一个 span 壳（不能直接套 Tooltip：
          Tooltip 是组件、透不出 DOM ref），悬浮提示挂在壳内的按钮上。 */}
      <span className="bcc-collect-send-trigger">
        {/* 提示的显隐由本组件掌控，不用 Tooltip 的 disabled 去压：disabled 会把 hover 监听整体摘掉，
            摘掉期间鼠标离开按钮收不到 mouseleave，提示的内部状态就停在「显示」且再也收不回；
            浮层展开期间还要忽略 hover 变化，否则关掉浮层时提示会跟着冒出来（鼠标一直在按钮上不会再触发 mouseenter）。 */}
        <Tooltip
          content="收号：选择字段下发给客户输入"
          popupVisible={tipOpen && !open}
          onVisibleChange={(visible) => {
            if (!open) setTipOpen(visible);
          }}
        >
          {/* 文字形态：图标表达「收号」不够直观，直接显示「收号」两个字；保持 28px 行高，
              与相邻的举手/铃铛同一基线。 */}
          <Button
            type="text"
            size="small"
            className="bcc-collect-send-text"
            aria-label="收号"
            onClick={() => setTipOpen(false)}
          >
            收号
          </Button>
        </Tooltip>
      </span>
    </Popover>
  );
}
